import React, { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router';

import { FileXmark } from '@gravity-ui/icons';
import FileArrowUp from '@gravity-ui/icons/FileArrowUp';
import {
    Button,
    Checkbox,
    ClipboardButton,
    Flex,
    Icon,
    Link as GravityLink,
    Loader,
    Pagination as GravityPagination,
    Tabs,
    Text,
    useToaster,
} from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import {
    deleteSkillImage,
    deleteSkillSound,
    getResourcesStatus,
    getSkillImages,
    getSkillSound,
    getSkillSounds,
    uploadSkillImage,
    uploadSkillSound,
} from 'client/api';
import { ErrorBoundary } from 'client/blocks/ErrorBoundary/ErrorBoundary';
import { getPoller } from 'client/blocks/ResourcesPage/Poller';
import { LinkMobile } from 'client/blocks-redesign/MenuMobile/LinkMobile';
import { AudioControl } from 'client/blocks-redesign/Resources/AudioControl';
import { moment } from 'client/lib/moment';
import { routes } from 'client/lib/routes';
import { formatBytes } from 'client/lib/utils';
import { Quota } from 'client/model/resources';
import { ImageResource, SoundResource } from 'client/model/resources';
import { AliceSkill } from 'client/model/skill';
import { ItemsPage } from 'client/types/utils';
import * as appFormKeyset from 'i18n/AppForm';
import * as keyset from 'i18n/Resources';
import { useDebouncedCallback } from 'use-debounce';
import { v4 as uuidv4 } from 'uuid';

import { PageLayout } from '../Layout/PageLayout';

import styles from './Resources.module.scss';

const RESOURCE_TOAST = 'RESOURCE_TOAST';

const t = i18n(keyset);
const tAppForm = i18n(appFormKeyset);

interface Props {
    skill: AliceSkill;
    disabled?: boolean;
}

enum ResourceTab {
    Images = 'images',
    Sounds = 'sounds',
}

interface ItemWrapper<T> {
    file?: File;
    item?: T;
    error?: ResourceError;
    clientId: string;
    selected?: boolean;
}

interface ResourceError {
    message: string;
    payload?: {
        type?: string;
    };
}

interface ResourceManagerProps<T extends { id: string }> {
    skill: { id: string };
    type: 'sounds' | 'images';
    getList: (page: number) => Promise<ItemsPage<T>>;
    onDelete: (ids: string[]) => Promise<void>;
    upload: (file: File) => Promise<T>;
    accept?: string | string[];
    uploadText: ReactNode;
    disabled?: boolean;
    children: (props: { items: Array<ItemWrapper<T>>; toggleSelect: (toggledId: string) => void }) => React.ReactNode;
}

function ResourceManager<T extends { id: string }>(props: ResourceManagerProps<T>) {
    const { type, getList, upload, children, accept, uploadText, onDelete, disabled } = props;
    const [quota, setQuota] = useState<Quota>();
    const [limit, setLimit] = useState(0);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [items, setItems] = useState<Array<ItemWrapper<T>>>([]);
    const [isListLoading, setIsListLoading] = useState(true);

    const { add } = useToaster();

    const updateQuota = useCallback(() => {
        getResourcesStatus()
            .then((res) => setQuota(res[type]))
            .catch((error) => {
                console.error(error);
                add({ title: tAppForm('unknown-error'), theme: 'danger', name: RESOURCE_TOAST });
            });
    }, [add, type]);

    const debouncedUpdateQuota = useDebouncedCallback(updateQuota, 1000);

    const loadAll = useCallback(
        async (showLoader = true) => {
            try {
                if (showLoader) {
                    setIsListLoading(true);
                }

                const itemsRes = await getList(page);
                setItems(itemsRes.items.map((x) => ({ item: x, clientId: x.id || uuidv4() })));
                setLimit(itemsRes.limit);
                setTotal(itemsRes.total);
            } catch (e) {
                console.error(e);
                add({ title: tAppForm('unknown-error'), theme: 'danger', name: RESOURCE_TOAST });
            } finally {
                setIsListLoading(false);
            }
        },
        [getList, page, add],
    );

    const debouncedLoadAll = useDebouncedCallback(() => void loadAll(false), 1000);

    useEffect(() => {
        void loadAll();
    }, [loadAll]);

    useEffect(() => {
        void updateQuota();
    }, [updateQuota]);

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            for (const file of acceptedFiles) {
                const wrp: ItemWrapper<T> = { file, clientId: uuidv4() };
                setTotal((prev) => prev + 1);

                upload(file)
                    .then((res) => {
                        wrp.item = res;
                        setItems((prev) => [...prev]);
                        debouncedUpdateQuota();
                    })
                    .catch((e) => {
                        wrp.error = e;
                        setItems((prev) => [...prev]);
                    });

                setItems((prev) => [wrp, ...prev]);
            }
        },
        [upload, debouncedUpdateQuota],
    );

    const toggleSelect = useCallback(
        (toggledId: string) => {
            const toggledIndex = items.findIndex((item) => item.clientId === toggledId);

            if (toggledIndex === -1) return;

            const toggledItem = items[toggledIndex];
            const updatedItems = [...items];
            updatedItems.splice(toggledIndex, 1, { ...toggledItem, selected: !toggledItem.selected });
            setItems(updatedItems);
        },
        [items],
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept,
        disabled,
    });

    const handleDelete = useCallback(
        (ids: string[]) => {
            const idsSet = new Set(ids);

            const idsForDelete: string[] = items
                .filter((item) => idsSet.has(item.clientId))
                .map((x) => (x.item ? x.item.id : ''))
                .filter((x) => !!x);

            setItems((prev) => {
                return prev.filter((x) => {
                    return !idsSet.has(x.clientId);
                });
            });

            return onDelete(idsForDelete)
                .then(() => {
                    debouncedLoadAll();
                    debouncedUpdateQuota();
                })
                .catch(console.error);
        },
        [items, onDelete, debouncedLoadAll, debouncedUpdateQuota],
    );

    const handleDeleteSelected = () => {
        handleDelete(items.filter((x) => x.selected).map((x) => x.clientId));
    };

    const handleDeleteAll = () => {
        handleDelete(items.map((x) => x.clientId));
    };

    const pageCount = limit > 0 ? Math.ceil(total / limit) : 0;

    return (
        <div className={styles.resourceManager}>
            <div className={styles.resources__controls}>
                <div className={styles.resources__controls}>
                    {quota ? (
                        <div>
                            {t('limit-size', {
                                used: formatBytes(quota.quota.used || 0),
                                total: formatBytes(quota.quota.total),
                            })}
                        </div>
                    ) : null}
                    <div {...getRootProps()} className={clx(styles.dropzone, isDragActive && styles.dropzoneActive)}>
                        <input {...getInputProps()} />
                        <Flex alignItems="center" gap={1}>
                            <Icon data={FileArrowUp} size={16} />
                            {uploadText}
                        </Flex>
                    </div>
                </div>
                <div className={styles.resources__controls}>
                    <Button onClick={handleDeleteSelected} view="flat-action" disabled={disabled}>
                        {tAppForm('delete')}
                    </Button>
                    <Button onClick={handleDeleteAll} view="flat-action" disabled={disabled}>
                        {tAppForm('delete-all')}
                    </Button>
                </div>
            </div>

            {isListLoading && (
                <div className={styles.loader}>
                    <Loader size="l" />
                </div>
            )}

            {total > 0 ? (
                <div>{children({ items, toggleSelect })}</div>
            ) : isListLoading ? null : (
                <div className={styles.noData}>
                    <Text>{t('error-not-loaded')}</Text>
                </div>
            )}

            {total > 0 && pageCount > 1 && (
                <Flex justifyContent="center" width="full">
                    <GravityPagination
                        page={page}
                        pageSize={limit}
                        total={total}
                        onUpdate={(newPage) => setPage(newPage)}
                    />
                </Flex>
            )}
        </div>
    );
}

const DocLink: FC<{ children: React.ReactNode; href: string }> = ({ children, href }) => {
    return (
        <GravityLink href={href} target="_blank">
            {children}
        </GravityLink>
    );
};

const ImageTable: FC<{
    items: Array<ItemWrapper<ImageResource>>;
    toggleSelect: (toggledId: string) => void;
}> = ({ items, toggleSelect }) => {
    const { url: avatarsUrl } = __appConfig.avatars;

    const renderError = (error: ResourceError): React.ReactNode => {
        const message = error.message;
        if (!message) {
            return <Text color="danger">{t('image-table-error')}</Text>;
        }

        if (message.indexOf('Quota exceeded') > -1) {
            return (
                <DocLink href="https://tech.yandex.ru/dialogs/alice/doc/resource-upload-docpage">
                    {t('image-table-error-limit')}
                </DocLink>
            );
        }

        if (error.payload && error.payload.type === 'MoreThenMaximum') {
            return (
                <DocLink href="https://tech.yandex.ru/dialogs/alice/doc/resource-upload-docpage">
                    {t('image-table-error-big-size')}
                </DocLink>
            );
        }

        if (error.payload && error.payload.type === 'LessThenMinimum') {
            return (
                <DocLink href="https://tech.yandex.ru/dialogs/alice/doc/resource-upload-docpage">
                    {t('image-table-error-low-size')}
                </DocLink>
            );
        }

        switch (message) {
            case 'Invalid image size':
                return (
                    <DocLink href="https://tech.yandex.ru/dialogs/alice/doc/resource-upload-docpage">
                        {t('image-table-error-wrong-size')}
                    </DocLink>
                );
            case 'Invalid image':
                return <Text color="danger">{t('image-table-error-incorrect-image')}</Text>;
            default:
                return <Text color="danger">{t('image-table-error')}</Text>;
        }
    };

    return (
        <table className={styles.resources__table}>
            <tbody>
                {items.map(({ item, error, clientId, selected }) => (
                    <tr key={clientId}>
                        <td className={styles.resources__selectionCheckboxCell}>
                            <Checkbox checked={!!selected} onUpdate={() => toggleSelect(clientId)} size="l" />
                        </td>
                        <td className={styles.resources__itemImageCell}>
                            {item ? (
                                <div
                                    className={styles.imagePreview}
                                    style={{
                                        backgroundImage: `url(${avatarsUrl}/get-dialogs-skill-card/${item.id}/orig)`,
                                    }}
                                />
                            ) : error ? (
                                <div className={styles.imagePreview}>
                                    <Icon data={FileXmark} className={styles.errorIcon} size={18} />
                                </div>
                            ) : (
                                <div className={styles.imagePreview}>
                                    <Loader size="s" />
                                </div>
                            )}
                        </td>
                        <td>
                            {item ? (
                                <>
                                    <div className={styles.resources__itemDataRow}>
                                        <div>{formatBytes(item.size)}</div>
                                        <span>{'·'}</span>
                                        <div>{moment(item.createdAt).format('DD.MM.YYYY')}</div>
                                    </div>
                                    <div className={styles.resources__itemActionBlock}>
                                        <div className={styles.resources__itemId}>{item.id}</div>
                                        <ClipboardButton text={item.id} size="s" />
                                    </div>
                                </>
                            ) : error ? (
                                <div className={styles.resources__itemDataRow}>{renderError(error)}</div>
                            ) : null}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

const SoundTable: FC<{
    items: Array<ItemWrapper<SoundResource>>;
    skillId: string;
    toggleSelect: (toggledId: string) => void;
}> = ({ items, skillId, toggleSelect }) => {
    const { staticResourcesUrl } = __appConfig.app;
    const [_, setState] = useState(0);

    useEffect(() => {
        const inProcess = items.filter((x) => x.item && !x.item.isProcessed && !x.item.error);
        const stop = getPoller(
            inProcess,
            (s) => getSkillSound(skillId, s),
            () => setState(new Date().getTime()),
        );

        return stop;
    }, [items, skillId]);

    const renderSoundError = (error: ResourceError): React.ReactNode => {
        if (!error.message) {
            return <Text color="danger">Произошла ошибка</Text>;
        }

        if (error.message.indexOf('Quota exceeded') > -1) {
            return (
                <DocLink href="https://yandex.ru/dev/dialogs/alice/doc/resource-sounds-upload-docpage">
                    {t('sounds-quota-is-exceed')}
                </DocLink>
            );
        }

        switch (error.message) {
            case 'File is too large':
                return (
                    <DocLink href="https://yandex.ru/dev/dialogs/alice/doc/resource-sounds-upload-docpage">
                        {t('image-table-error-big-size')}
                    </DocLink>
                );
            default:
                return <Text color="danger">{tAppForm('unknown-error')}</Text>;
        }
    };

    return (
        <table className={styles.resources__table}>
            <tbody>
                {items.map(({ item, error, clientId, selected }) => (
                    <tr key={clientId}>
                        <td className={styles.resources__selectionCheckboxCell}>
                            <Checkbox checked={!!selected} onUpdate={() => toggleSelect(clientId)} size="l" />
                        </td>
                        <td className={styles.resources__itemImageCell}>
                            {item ? (
                                <AudioControl
                                    src={`${staticResourcesUrl}/dialogs/dialogs-upload/sounds/opus/${skillId}/${item.id}.opus`}
                                />
                            ) : error ? (
                                <div className={styles.imagePreview}>
                                    <Icon data={FileXmark} className={styles.errorIcon} size={18} />
                                </div>
                            ) : (
                                <div className={styles.imagePreview}>
                                    <Loader size="s" />
                                </div>
                            )}
                        </td>
                        <td>
                            {item ? (
                                item.isProcessed ? (
                                    <>
                                        <div className={styles.resources__itemDataRow}>
                                            <div>{item.originalName}</div>
                                            <span>{'·'}</span>
                                            <div>{formatBytes(item.size)}</div>
                                            <span>{'·'}</span>
                                            <div>{moment(item.createdAt).format('DD.MM.YYYY')}</div>
                                        </div>
                                        <div className={styles.resources__itemActionBlock}>
                                            <div className={styles.resources__itemId}>{item.id}</div>
                                            <ClipboardButton text={item.id} size="s" />
                                        </div>
                                    </>
                                ) : item.error ? (
                                    <div className={styles.resources__itemDataRow}>
                                        <Text color="danger">{t('sounds-process-error')}</Text>
                                    </div>
                                ) : (
                                    <div className={styles.resources__itemDataRow}>
                                        <Text>{t('sounds-in-process')}</Text>
                                    </div>
                                )
                            ) : error ? (
                                renderSoundError(error)
                            ) : null}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

export const Resources: FC<Props> = ({ skill, disabled = false }) => {
    const [activeTab, setActiveTab] = useState<ResourceTab>(ResourceTab.Images);

    const { add: addToast } = useToaster();

    const location = useLocation();
    const history = useHistory();
    const allowUploadSounds = Boolean(__appConfig.user.featureFlags.allowUploadSounds);

    const handleTabSelection = useCallback(
        (value: ResourceTab) => {
            switch (value) {
                case ResourceTab.Images:
                    history.replace(routes.app.skillResources('images', skill.id));
                    break;
                case ResourceTab.Sounds:
                    history.replace(routes.app.skillResources('sounds', skill.id));
                    break;
            }
        },
        [history, skill.id],
    );

    const handleGetImagesList = useCallback((page: number) => getSkillImages(skill.id, page), [skill.id]);

    const handleGetSoundsList = useCallback((page: number) => getSkillSounds(skill.id, page), [skill.id]);

    useEffect(() => {
        if (location.pathname.includes('/images')) {
            setActiveTab(ResourceTab.Images);
        } else if (location.pathname.includes('/sounds')) {
            setActiveTab(ResourceTab.Sounds);
        }
    }, [location.pathname]);

    const deleteImages = useCallback(
        async (ids: string[]) => {
            // TODO: move to new api endpoints PASKILLS-10042
            return new Promise<void>((res, rej) =>
                Promise.all(ids.map((id) => deleteSkillImage(skill.id, id)))
                    .then(() => res())
                    .catch((err) => {
                        console.error(err);
                        addToast({ title: t('delete-error'), theme: 'danger', name: RESOURCE_TOAST });
                        rej();
                    }),
            );
        },
        [addToast, skill.id],
    );

    const deleteSounds = useCallback(
        async (ids: string[]) => {
            // TODO: move to new api endpoints PASKILLS-10042
            return new Promise<void>((res, rej) =>
                Promise.all(ids.map((id) => deleteSkillSound(skill.id, id)))
                    .then(() => res())
                    .catch((err) => {
                        console.error(err);
                        addToast({ title: t('delete-error'), theme: 'danger', name: RESOURCE_TOAST });
                        rej();
                    }),
            );
        },
        [addToast, skill.id],
    );

    const tabItems = [
        {
            id: ResourceTab.Images,
            title: t('menu-images'),
        },
    ];

    if (allowUploadSounds) {
        tabItems.push({
            id: ResourceTab.Sounds,
            title: t('menu-sounds'),
        });
    }

    return (
        <ErrorBoundary withHomepageLink>
            {activeTab === ResourceTab.Sounds && (
                <LinkMobile
                    to={routes.app.skillResources(ResourceTab.Images, skill.id)}
                    text={t('menu-images')}
                    isCurrent
                />
            )}
            <LinkMobile
                to={routes.app.skillResources(activeTab, skill.id)}
                text={activeTab === ResourceTab.Images ? t('menu-images') : t('menu-sounds')}
                isCurrent
            />

            <PageLayout aliceApp={skill} pageTitle={tAppForm('resources')}>
                <Tabs
                    className={styles.tabs}
                    activeTab={activeTab}
                    items={tabItems}
                    onSelectTab={handleTabSelection}
                    size="l"
                />
                <Switch>
                    <Route
                        path={routes.app.skillResources('images')}
                        render={() => (
                            <ResourceManager
                                key={`${skill.id}__images`}
                                type="images"
                                skill={skill}
                                getList={handleGetImagesList}
                                upload={(f) => uploadSkillImage(skill.id, f)}
                                onDelete={deleteImages}
                                accept="image/*"
                                uploadText={t('add-file')}
                                disabled={disabled}
                            >
                                {({ items, toggleSelect }) => <ImageTable items={items} toggleSelect={toggleSelect} />}
                            </ResourceManager>
                        )}
                    />
                    {allowUploadSounds && (
                        <Route
                            path={routes.app.skillResources('sounds')}
                            render={() => (
                                <ResourceManager
                                    key={`${skill.id}__sounds`}
                                    type="sounds"
                                    skill={skill}
                                    getList={handleGetSoundsList}
                                    upload={(f) => uploadSkillSound(skill.id, f)}
                                    onDelete={deleteSounds}
                                    accept="audio/*"
                                    uploadText={t('add-file')}
                                    disabled={disabled}
                                >
                                    {({ items, toggleSelect }) => (
                                        <SoundTable items={items} skillId={skill.id} toggleSelect={toggleSelect} />
                                    )}
                                </ResourceManager>
                            )}
                        />
                    )}

                    <Redirect
                        exact
                        from={routes.app.skillResources('', skill.id)}
                        to={routes.app.skillResources('images', skill.id)}
                    />
                </Switch>
            </PageLayout>
            {allowUploadSounds && activeTab === ResourceTab.Images && (
                <LinkMobile
                    to={routes.app.skillResources(ResourceTab.Sounds, skill.id)}
                    text={t('menu-sounds')}
                    isCurrent
                />
            )}
        </ErrorBoundary>
    );
};
